import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import ThemeToggle from './components/ThemeToggle';
import LanguageToggle from './components/LanguageToggle';
import { fetchUserProfile, useLogout, useOutsideClick } from './common';
import { adminAPI } from './api';
import { GlobalStyle, DropdownItem, LogoutButton, MenuButton, DropdownMenu } from './styles/GlobalStyles';
import { useT } from './LocaleContext';
import {
  AdminPageContainer,
  Brand,
  Subtitle,
  Directory,
  ListHead,
  Search,
  UserBlock,
  UserRow,
  UserName,
  UserEmail,
  UserMeta,
  ToggleButton,
  UserActions,
  GhostButton,
  SectionTitle,
  CallRow,
  Notice,
  BanForm,
  BanInput,
  Empty,
} from './styles/AdminPageStyles';

const AdminPage = () => {
  const t = useT();
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [liveCalls, setLiveCalls] = useState([]);
  const [bans, setBans] = useState([]);
  const [banEmail, setBanEmail] = useState('');
  const [query, setQuery] = useState('');
  const [notice, setNotice] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const handleLogout = useLogout();

  useOutsideClick(dropdownRef, () => setMenuOpen(false));

  const statusLabel = (item) => {
    const bits = [];
    if (item.is_admin) {
      bits.push(t('admin.statusAdmin'));
    } else if (item.is_active) {
      bits.push(t('admin.statusActive'));
    } else {
      bits.push(t('admin.statusDisabled'));
    }
    if (item.is_banned) {
      bits.push(t('admin.statusBanned'));
    }
    if (item.is_online) {
      bits.push(t('admin.statusOnline'));
    }
    return bits.join(' · ');
  };

  const refresh = useCallback(async () => {
    const [overviewRes, usersRes, callsRes, bansRes] = await Promise.all([
      adminAPI.getOverview(),
      adminAPI.getUsers(),
      adminAPI.getCalls(),
      adminAPI.getBans(),
    ]);
    setOverview(overviewRes.data);
    setUsers(usersRes.data.users || []);
    setLiveCalls(callsRes.data.live || []);
    setBans(bansRes.data.bans || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const profile = await fetchUserProfile();
        if (cancelled) {
          return;
        }
        setUser(profile);
        if (!profile?.is_admin) {
          setReady(true);
          return;
        }
        await refresh();
      } catch (_) {
        if (!cancelled) {
          navigate('/login', { replace: true });
          return;
        }
      } finally {
        if (!cancelled) {
          setReady(true);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [navigate, refresh]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return users;
    }
    return users.filter((item) => (
      (item.user_name || '').toLowerCase().includes(needle)
      || (item.email || '').toLowerCase().includes(needle)
      || (item.connect_id || '').includes(needle)
    ));
  }, [query, users]);

  const replaceUser = (updated) => {
    setUsers((prev) => prev.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)));
  };

  const runUserAction = async (item, action) => {
    if (item.is_admin && action !== 'none') {
      return;
    }
    setBusyId(item.id);
    try {
      if (action === 'toggle') {
        const response = await adminAPI.setUserActive(item.id, !item.is_active);
        replaceUser(response.data);
      } else if (action === '2fa') {
        if (!window.confirm(t('admin.confirmReset2fa', { name: item.user_name }))) {
          return;
        }
        const response = await adminAPI.reset2fa(item.id);
        replaceUser(response.data);
      } else if (action === 'kick') {
        if (!window.confirm(t('admin.confirmKick', { name: item.user_name }))) {
          return;
        }
        const response = await adminAPI.kickUser(item.id);
        replaceUser(response.data);
        await refresh();
      } else if (action === 'code') {
        const response = await adminAPI.issueResetCode(item.id);
        setNotice(t('admin.resetCodeNotice', { email: item.email, code: response.data.code }));
      } else if (action === 'ban') {
        if (!window.confirm(t('admin.confirmBan', { email: item.email }))) {
          return;
        }
        const response = await adminAPI.banUser(item.id);
        replaceUser(response.data);
        await refresh();
      } else if (action === 'delete') {
        if (!window.confirm(t('admin.confirmDelete', { name: item.user_name, email: item.email }))) {
          return;
        }
        await adminAPI.deleteUser(item.id, true);
        setUsers((prev) => prev.filter((row) => row.id !== item.id));
        await refresh();
      }
    } catch (_) {
      setNotice(t('admin.actionFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const endCall = async (call) => {
    if (!window.confirm(t('admin.confirmEndCall'))) {
      return;
    }
    setBusyId(call.id);
    try {
      await adminAPI.endCall(call.id);
      await refresh();
    } catch (_) {
      setNotice(t('admin.endCallFailed'));
    } finally {
      setBusyId(null);
    }
  };

  if (!ready) {
    return (
      <AdminPageContainer>
        <GlobalStyle />
        <Empty>{t('common.loading')}</Empty>
      </AdminPageContainer>
    );
  }

  if (!user?.is_admin) {
    return <Navigate to="/" replace />;
  }

  const summary = overview
    ? t('admin.summary', {
      users: overview.users,
      online: overview.online,
      live: overview.live_calls + overview.live_rooms,
    })
    : t('admin.brand');

  return (
    <AdminPageContainer>
      <GlobalStyle />
      <MenuButton onClick={() => setMenuOpen((open) => !open)} $open={menuOpen}>
        {user.email.slice(0, 2).toUpperCase() || '≡'}
      </MenuButton>
      <DropdownMenu ref={dropdownRef} open={menuOpen}>
        <DropdownItem to="/">{t('nav.home')}</DropdownItem>
        <DropdownItem to="/chats">{t('nav.chats')}</DropdownItem>
        <DropdownItem to="/profile">{t('nav.profile')}</DropdownItem>
        <ThemeToggle />
        <LanguageToggle />
        <LogoutButton onClick={handleLogout}>{t('nav.logout')}</LogoutButton>
      </DropdownMenu>

      <Brand>{t('admin.brand')}</Brand>
      <Subtitle>{summary}</Subtitle>

      <Directory>
        {notice ? <Notice>{notice}</Notice> : null}
        <Search
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('admin.searchUsers')}
          aria-label={t('admin.searchUsers')}
        />
        {filtered.length === 0 ? (
          <Empty>{t('admin.noUsers')}</Empty>
        ) : (
          <>
            <ListHead>
              <span>{t('admin.colName')}</span>
              <span>{t('admin.colEmail')}</span>
              <span>{t('admin.colStatus')}</span>
              <span />
            </ListHead>
            {filtered.map((item) => (
              <UserBlock key={item.id}>
                <UserRow>
                  <UserName>{item.user_name || t('admin.unnamed')}</UserName>
                  <UserEmail>{item.email}</UserEmail>
                  <UserMeta>{statusLabel(item)}</UserMeta>
                  <ToggleButton
                    type="button"
                    $off={item.is_active}
                    disabled={item.is_admin || busyId === item.id}
                    onClick={() => runUserAction(item, 'toggle')}
                  >
                    {item.is_admin ? '—' : (item.is_active ? t('admin.disable') : t('admin.enable'))}
                  </ToggleButton>
                </UserRow>
                {!item.is_admin && (
                  <UserActions>
                    <GhostButton
                      type="button"
                      disabled={!item.is_2fa_enabled || busyId === item.id}
                      onClick={() => runUserAction(item, '2fa')}
                    >
                      {t('admin.reset2fa')}
                    </GhostButton>
                    <GhostButton
                      type="button"
                      disabled={busyId === item.id}
                      onClick={() => runUserAction(item, 'kick')}
                    >
                      {t('admin.kickSession')}
                    </GhostButton>
                    <GhostButton
                      type="button"
                      disabled={busyId === item.id}
                      onClick={() => runUserAction(item, 'code')}
                    >
                      {t('admin.resetCode')}
                    </GhostButton>
                    <GhostButton
                      type="button"
                      $danger
                      disabled={item.is_banned || busyId === item.id}
                      onClick={() => runUserAction(item, 'ban')}
                    >
                      {t('admin.banEmail')}
                    </GhostButton>
                    <GhostButton
                      type="button"
                      $danger
                      disabled={busyId === item.id}
                      onClick={() => runUserAction(item, 'delete')}
                    >
                      {t('admin.delete')}
                    </GhostButton>
                  </UserActions>
                )}
              </UserBlock>
            ))}
          </>
        )}

        <SectionTitle>{t('admin.bannedEmails')}</SectionTitle>
        <BanForm
          onSubmit={async (event) => {
            event.preventDefault();
            const email = banEmail.trim().toLowerCase();
            if (!email) {
              return;
            }
            try {
              await adminAPI.addBan(email);
              setBanEmail('');
              await refresh();
            } catch (_) {
              setNotice(t('admin.actionFailed'));
            }
          }}
        >
          <BanInput
            type="email"
            value={banEmail}
            onChange={(event) => setBanEmail(event.target.value)}
            placeholder={t('admin.emailToBan')}
            aria-label={t('admin.emailToBan')}
          />
          <GhostButton type="submit" $danger>{t('admin.ban')}</GhostButton>
        </BanForm>
        {bans.length === 0 ? (
          <Empty>{t('admin.noBanned')}</Empty>
        ) : (
          bans.map((row) => (
            <CallRow key={row.email}>
              <UserName>{row.email}</UserName>
              <UserMeta>{t('admin.statusBanned')}</UserMeta>
              <ToggleButton
                type="button"
                disabled={busyId === row.email}
                onClick={async () => {
                  setBusyId(row.email);
                  try {
                    await adminAPI.removeBan(row.email);
                    await refresh();
                  } catch (_) {
                    setNotice(t('admin.actionFailed'));
                  } finally {
                    setBusyId(null);
                  }
                }}
              >
                {t('admin.unban')}
              </ToggleButton>
            </CallRow>
          ))
        )}

        <SectionTitle>{t('admin.liveCalls')}</SectionTitle>
        {liveCalls.length === 0 ? (
          <Empty>{t('admin.noLiveCalls')}</Empty>
        ) : (
          liveCalls.map((call) => (
            <CallRow key={call.id}>
              <UserName>{call.label || call.kind}</UserName>
              <UserMeta>{call.kind} · {call.state}</UserMeta>
              <ToggleButton
                type="button"
                $off
                disabled={busyId === call.id}
                onClick={() => endCall(call)}
              >
                {t('admin.end')}
              </ToggleButton>
            </CallRow>
          ))
        )}
      </Directory>
    </AdminPageContainer>
  );
};

export default AdminPage;
